import classNames from 'classnames'
import { FC, PropsWithChildren } from 'react'

type Props = TitleProps & PropsWithChildren<any>

export interface TitleProps {
	level?: '1' | '2' | '3' | '4' | '5' | '6'
	size?: string
	color?: string
	bold?: boolean
	className?: string
	testId: string
}

/**
 * A reusable component for rendering titles with customizable styles and attributes.
 *
 * @typedef {Object} TitleProps
 * @property {('1'|'2'|'3'|'4'|'5'|'6')} [level='1'] - The HTML heading level for the title.
 * @property {string} [size='xl'] - The size of the title text.
 * @property {string} [color='black'] - The color of the title text.
 * @property {boolean} [bold=false] - Whether the title text should be bold.
 * @property {string} [className] - Additional CSS classes to apply to the title element.
 * @property {string} testId - The data-testid attribute for testing purposes.
 *
 * @param {TitleProps} props - The props for the Title component.
 * @returns {JSX.Element} - The rendered Title component.
 */
const Title: FC<Props> = ({
	level = '1',
	color = 'black',
	size = 'xl',
	bold,
	children,
	className,
	testId,
}) => {
	const HTag = `h${level}` as keyof JSX.IntrinsicElements

	return (
		<HTag
			className={classNames(
				{ 'font-bold': bold },
				`text-${size} text-${color}`,
				className
			)}
			data-test-id={testId}
		>
			{children}
		</HTag>
	)
}

export default Title
